import { useWhiteLabelContext } from 'components/white-label/context';
import { EnvVariable, getEnv } from 'lib/helpers/envHelper';
import { Organization } from 'lib/types/company';
import _ from 'lodash';
import { ReactNode } from 'react';
import { useAuthContext } from './context';
import { FeatureName, PermissionName } from './types';

export interface IRestrictedProps {
  children: ReactNode;
  /**
   * Content restricted from features. This children will not be rendered if any
   * of these features are enabled for the company.
   */
  fromFeatures?: FeatureName[];
  /**
   * Content restricted from environment. This children will not be rendered if
   * the environment is set to this value.
   */
  fromEnvironment?: 'production' | 'developer';
  /**
   * Content restricted from organization. This children will not be rendered if
   * the user has this organization.
   */
  fromOrganization?: Organization;
  /**
   * Content restricted from permission. This children will not be rendered if
   * the user has this permission.
   */
  fromPermission?: PermissionName;
  /**
   * Content restricted to environment. The
   * children will be rendered if the environment is set to this value.
   */
  toEnvironment?: 'production' | 'developer';
  /**
   * Content restricted to features. The children will be rendered if any of
   * these features are enabled for the company.
   */
  toFeatures?: FeatureName[];
  /**
   * Content restricted to organization. This children will be rendered if
   * the user has this organization.
   */
  toOrganizations?: Organization[];
  /**
   * Content restricted to permission. The children will be rendered if the user
   * has this permission.
   */
  toPermission?: PermissionName;
}

export const Restricted = ({
  children,
  fromFeatures,
  fromEnvironment,
  fromOrganization,
  fromPermission,
  toEnvironment,
  toFeatures,
  toOrganizations,
  toPermission,
}: IRestrictedProps) => {
  const { getIsInPermission, getIsFeatureEnabled } = useAuthContext();
  const { organization } = useWhiteLabelContext();

  let isRendered = true;
  if (fromEnvironment) {
    isRendered =
      isRendered && getEnv(EnvVariable.ENVIRONMENT) !== fromEnvironment;
  }
  if (fromFeatures) {
    // TODO (Post AR Permissions): Update to single check.
    isRendered = isRendered && !_.some(fromFeatures, getIsFeatureEnabled);
  }
  if (fromOrganization) {
    isRendered = isRendered && organization !== fromOrganization;
  }
  if (fromPermission) {
    isRendered = isRendered && !getIsInPermission(fromPermission);
  }
  if (toEnvironment) {
    isRendered =
      isRendered && getEnv(EnvVariable.ENVIRONMENT) === toEnvironment;
  }
  if (toFeatures) {
    // TODO (Post AR Permissions): Update to single check.
    isRendered = isRendered && _.some(toFeatures, getIsFeatureEnabled);
  }
  if (toOrganizations) {
    isRendered = isRendered && _.includes(toOrganizations, organization);
  }
  if (toPermission) {
    isRendered = isRendered && getIsInPermission(toPermission);
  }

  return isRendered ? <>{children}</> : null;
};
